/**
 * The five sidebar bands, top to bottom: logo, workspace row, scrolling
 * nav, alerts, fixed 136px footer (account row, launcher lane, collapse
 * toggle). 280px expanded / 72px collapsed.
 * Spec: assets/handoffs/design_handoff_app_shell, Rules — shell / support
 * launcher / collapse.
 *
 * @param {boolean=false} collapsed
 * @param {function=} onToggleCollapse
 * @param {Array} workspaces
 * @param {Object} currentWorkspace
 * @param {function} onSwitchWorkspace
 * @param {{top,widgets,reports,standalone,platformSettings}} nav - top/standalone
 *   are flat item arrays; widgets/reports/platformSettings are
 *   {label,icon,children} groups, any of which may be omitted
 * @param {string} activeKey
 * @param {Array} alerts
 * @param {function} onDismissAlert
 * @param {Object} account
 * @param {boolean=false} impersonating
 * @param {function} renderAccountMenu - (close) => ReactElement
 * @param {string} branding - appConfig.branding, for the logo asset path
 */
import React, { useCallback, useLayoutEffect, useRef } from "react";
import { Link } from "react-router-dom";
import classNames from "classnames";
import Icon from "../../../theme/untitled/icons/Icon";
import SidebarNavItem from "../SidebarNavItem/SidebarNavItem";
import SidebarNavParent from "../SidebarNavParent/SidebarNavParent";
import WorkspaceSwitcher from "../WorkspaceSwitcher/WorkspaceSwitcher";
import AccountRow from "../AccountRow/AccountRow";
import SidebarTooltip from "../SidebarTooltip/SidebarTooltip";
import SidebarAlerts from "../../PageTemplate/SidebarNotifications/SidebarAlerts";
import { useDismissablePanel } from "../useDismissablePanel";
import styles from "./Sidebar.module.scss";

// Every page wraps itself in its own PageTemplate/AppShell/Sidebar instance
// (see PageTemplate/index.js), so React Router unmounts and remounts this
// whole component on every navigation — a fresh DOM node always starts
// scrolled to the top. Persisting the offset in a module-level variable
// (survives remounts, since the module itself isn't re-evaluated) and
// restoring it on mount keeps the nav's scroll position stable across
// page changes instead of resetting it every time.
let lastNavScrollTop = 0;

const CollapsedAlerts = ({ alerts, onDismiss }) => {
  const { open, setOpen, triggerRef, panelRef, close } = useDismissablePanel();
  if (!alerts.length) return null;

  const hasCritical = alerts.some((a) => a.variant === "critical" || a.variant === "warning");

  return (
    <div className={styles.collapsedAlertsWrap}>
      <button
        ref={triggerRef}
        type="button"
        className={styles.collapsedAlertsButton}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <Icon name="AlertCircle" className={styles.collapsedAlertsIcon} />
        {hasCritical && <span className={styles.collapsedAlertsDot} />}
      </button>
      {open && (
        <div ref={panelRef} className={styles.collapsedAlertsPanel}>
          <SidebarAlerts alerts={alerts} onDismiss={(id) => { onDismiss(id); close(false); }} />
        </div>
      )}
    </div>
  );
};

const Sidebar = ({
  collapsed = false,
  onToggleCollapse,
  workspaces = [],
  currentWorkspace,
  onSwitchWorkspace,
  nav,
  activeKey,
  alerts = [],
  onDismissAlert,
  account,
  impersonating = false,
  renderAccountMenu,
  branding = "revisual",
}) => {
  const navRef = useRef(null);

  useLayoutEffect(() => {
    if (navRef.current) {
      navRef.current.scrollTop = lastNavScrollTop;
    }
  }, []);

  const onNavScroll = useCallback((e) => {
    lastNavScrollTop = e.target.scrollTop;
  }, []);

  return (
    <aside className={classNames(styles.root, { [styles["root--collapsed"]]: collapsed })}>
      <div className={styles.logo}>
        {collapsed ? (
          <Link to="/dashboard">
            <img src={`/img/branding/${branding}/logo_icon.png`} height="30" alt="" />
          </Link>
        ) : (
          <Link to="/dashboard">
            <img src={`/img/branding/${branding}/full_length_logo.png`} height="30" alt="" />
          </Link>
        )}
      </div>

      <WorkspaceSwitcher
        workspaces={workspaces}
        current={currentWorkspace}
        onSwitch={onSwitchWorkspace}
        collapsed={collapsed}
        disabled={impersonating}
      />

      <nav className={styles.nav} aria-label="Main" ref={navRef} onScroll={onNavScroll}>
        <ul className={styles.navList}>
          {nav.top?.map((item) => (
            <li key={item.key}>
              <SidebarNavItem
                label={item.label}
                icon={item.icon}
                to={item.to || `/${item.key}`}
                active={item.key === activeKey}
                collapsed={collapsed}
              />
            </li>
          ))}

          {nav.widgets && (
            <SidebarNavParent
              label={nav.widgets.label}
              icon={nav.widgets.icon}
              children={nav.widgets.children}
              activeKey={activeKey}
              collapsed={collapsed}
            />
          )}

          {nav.reports && (
            <SidebarNavParent
              label={nav.reports.label}
              icon={nav.reports.icon}
              children={nav.reports.children}
              activeKey={activeKey}
              collapsed={collapsed}
            />
          )}

          {nav.standalone?.map((item) => (
            <li key={item.key}>
              <SidebarNavItem
                label={item.label}
                icon={item.icon}
                to={item.to || `/${item.key}`}
                active={item.key === activeKey}
                collapsed={collapsed}
              />
            </li>
          ))}

          {nav.platformSettings && (
            <SidebarNavParent
              label={nav.platformSettings.label}
              icon={nav.platformSettings.icon}
              children={nav.platformSettings.children}
              activeKey={activeKey}
              collapsed={collapsed}
            />
          )}
        </ul>
      </nav>

      <div className={styles.alerts}>
        {collapsed ? (
          <CollapsedAlerts alerts={alerts} onDismiss={onDismissAlert} />
        ) : (
          <SidebarAlerts alerts={alerts} onDismiss={onDismissAlert} />
        )}
      </div>

      <div className={styles.footer}>
        <AccountRow
          account={account}
          collapsed={collapsed}
          impersonating={impersonating}
          renderMenu={renderAccountMenu}
        />

        <div className={styles.launcherLane}>
          {collapsed ? (
            <SidebarTooltip label="Expand">
              <button
                type="button"
                className={styles.collapseToggle}
                aria-label="Expand sidebar"
                onClick={onToggleCollapse}
              >
                <Icon name="ChevronRightDouble" className={styles.collapseIcon} />
              </button>
            </SidebarTooltip>
          ) : (
            <button
              type="button"
              className={styles.collapseToggle}
              aria-label="Collapse sidebar"
              onClick={onToggleCollapse}
            >
              <Icon name="ChevronLeftDouble" className={styles.collapseIcon} />
            </button>
          )}
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;
